import { Box, Center, ChakraProvider, CircularProgress, extendTheme, Fade, Image } from '@chakra-ui/react';
import { IAgreement, ISubscription } from '@partiu/api/dashboard/types.ts';
import { parseAgreementToThemeDash } from '@partiu/api/landing/utils.ts';
import { IUser } from '@partiu/api/types';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LoadingScreen } from '../components/organisms/LoadingScreen.tsx';
import { DialogProvider } from './DialogProvider.tsx';
import { axiosInstance, axiosInstanceV2 } from '@partiu/api/axios.ts';
import { getAgreementData } from '@partiu/api/dashboard/api';
import { IThemeContext, ILoadingContext, ISetupState } from './types';

const ANIMATION_TIMINGS = {
  SETUP_ANIMATE: 2000,
  SETUP_EXIT: 3000,
  REFRESH_CHILDREN: 1000,
} as const;

const defaultThemeContextData: IThemeContext = {
  id: null,
  name: '',
  logo: '',
  extraData: {},
  email: 'suporte@partiu.com.br',
  telephone: '(21) 97420-4416',
  colors: {
    primaryColor: '#fb8c00',
    primaryFontColor: '#ffffff',
    secondaryColor: '#ffffff',
    secondaryFontColor: '#ffffff',
    menuColor: '#ffffff',
    menuFontColor: '#ffffff',
    graphColors: [],
  },
  banners: [],
  bookstoreLabels: {
    prefix: '',
    prefixPlural: '',
    prefixedPlural: '',
    prefixedSingular: '',
    singular: '',
    plural: '',
  },
  integrationId: null,
  slug_app_ios: '',
  childrenList: [],
  bookstoreTypes: [],
  isIOS: false,
  sendBookEmail: false,
  enableSendingBookEmail: false,
  totalImportedUsers: 0,
  totalUsers: 0,
  slug_app_android: '',
  isMenuOpen: false,
  isSidebarCompact: false,
  setMenuOpen: () => {},
  setSidebarCompact: () => {},
  updateTheme: () => {},
  resetTheme: () => {},
  setLoading: () => {},
  isSetup: false,
  allowCpfLoginMethod: false,
  allowPromocodeLoginMethod: false,
  isApp: false,
  changeTheme: () => {},
  getValidSubscriptions: function (): ISubscription[] {
    throw new Error('Function not implemented.');
  },
};

const ThemeContext = createContext(defaultThemeContextData);

const setAxiosHeaders = (agreementId: number | null) => {
  axiosInstance.defaults.headers.common['x-agreement-id'] = agreementId;
  axiosInstanceV2.defaults.headers.common['x-agreement-id'] = agreementId;
};

const getUrlData = () => {
  const params = Object.fromEntries(new URLSearchParams(location.search));
  const urlData = {
    isApp: params.isApp === 'true',
    isIOS: params.isIOS === 'true',
  };

  const existingUrlData = JSON.parse(localStorage.getItem('urlData') || 'null');

  if (existingUrlData) {
    if (!params.isApp) urlData.isApp = existingUrlData.isApp;
    if (!params.isIOS) urlData.isIOS = existingUrlData.isIOS;
  }

  localStorage.setItem('urlData', JSON.stringify(urlData));
  return urlData;
};

const createSetupAnimations = (
  setState: React.Dispatch<React.SetStateAction<{
    themeData: IThemeContext;
    setupData: ISetupState;
    loadingData: ILoadingContext;
    urlData: { isApp: boolean; isIOS: boolean };
    refreshChildren: boolean;
  }>>
) => {
  const animateTimeout = setTimeout(() => {
    setState(prev => ({ ...prev, setupData: { ...prev.setupData, animate: true } }));
  }, ANIMATION_TIMINGS.SETUP_ANIMATE);

  const exitTimeout = setTimeout(() => {
    setState(prev => ({ ...prev, setupData: { ...prev.setupData, exit: true } }));
  }, ANIMATION_TIMINGS.SETUP_EXIT);

  return () => {
    clearTimeout(animateTimeout);
    clearTimeout(exitTimeout);
  };
};

const getInitialSidebarCompact = () => {
  try {
    return JSON.parse(localStorage.getItem('sidebarCompact') || 'false');
  } catch (error) {
    console.warn('Failed to load sidebar compact preference from localStorage:', error);
    return false;
  }
};

export function ThemeProvider({
  children,
  themeResolver,
}: { themeResolver: () => Promise<Partial<IThemeContext>> } & PropsWithChildren) {
  const [state, setState] = useState({
    themeData: { ...defaultThemeContextData, isSidebarCompact: getInitialSidebarCompact() },
    setupData: { isSetup: false, animate: false, exit: false } as ISetupState,
    loadingData: { isLoading: false, loadingDelay: 0, loadingDescription: 'Carregando...' } as ILoadingContext,
    urlData: { isApp: false, isIOS: false },
    refreshChildren: false,
  });

  const theme = useMemo(
    () =>
      extendTheme({
        colors: {
          ...state.themeData.colors,
        },
      }),
    [state.themeData.colors]
  );

  const updateTheme = useCallback((newThemeData: Partial<IThemeContext>) => {
    setState(prev => ({
      ...prev,
      themeData: { ...prev.themeData, ...newThemeData },
    }));
  }, []);

  const setLoading = useCallback((
    value: boolean,
    description: string = '',
    delay: number = state.loadingData.loadingDelay
  ) => {
    setState(prev => ({
      ...prev,
      loadingData: { isLoading: value, loadingDescription: description, loadingDelay: delay },
    }));
  }, [state.loadingData.loadingDelay]);

  const setMenuOpen = useCallback((value: boolean) => {
    setState(prev => ({
      ...prev,
      themeData: { ...prev.themeData, isMenuOpen: value },
    }));
  }, []);

  const setSidebarCompact = useCallback((value: boolean) => {
    try {
      localStorage.setItem('sidebarCompact', JSON.stringify(value));
    } catch (error) {
      console.warn('Failed to save sidebar compact preference to localStorage:', error);
    }
    setState(prev => ({
      ...prev,
      themeData: { ...prev.themeData, isSidebarCompact: value },
    }));
  }, []);

  const resetTheme = useCallback(() => {
    setState(prev => ({
      ...prev,
      themeData: defaultThemeContextData,
    }));
  }, []);

  const getValidSubscriptions = useCallback((profile: IUser | undefined | null): ISubscription[] => {
    if (!profile) return [];
    return profile.subscriptions
      .filter((sub) => sub.plan.agreement.id === state.themeData.id)
      .sort((a, b) => a.plan.level - b.plan.level);
  }, [state.themeData.id]);

  const changeTheme = useCallback((id: string) => {
    if (!state.themeData.childrenList || state.themeData.childrenList.length === 0) {
      return;
    }

    const theme = state.themeData.childrenList.find((child: { id: number | string }) => String(child.id) === String(id));

    if (!theme || theme.id === state.themeData.id) {
      return;
    }

    const originalChildrenList = state.themeData.childrenList;

    setState(prev => ({
      ...prev,
      refreshChildren: true,
      setupData: { isSetup: false, animate: false, exit: false },
    }));

    const newTheme = parseAgreementToThemeDash(theme as IAgreement);

    setState(prev => ({
      ...prev,
      themeData: {
        ...prev.themeData,
        ...newTheme,
        childrenList: originalChildrenList,
      },
    }));

    setAxiosHeaders(newTheme.id || null);
    localStorage.setItem('agreementId', JSON.stringify(newTheme.id));

    createSetupAnimations(setState);

    setTimeout(() => {
      setState(prev => ({ ...prev, refreshChildren: false }));
    }, ANIMATION_TIMINGS.REFRESH_CHILDREN);
  }, [state.themeData.childrenList, state.themeData.id]);

  useEffect(() => {
    const urlData = getUrlData();
    setState(prev => ({ ...prev, urlData }));
  }, []);

  // Sempre que o id do convênio mudar, garante que o header x-agreement-id
  // e o localStorage estejam sincronizados antes dos fetchs das páginas.
  useEffect(() => {
    const agreementId = state.themeData.id;
    if (!agreementId) return;

    setAxiosHeaders(agreementId);
    localStorage.setItem('agreementId', JSON.stringify(agreementId));
  }, [state.themeData.id]);

  useEffect(() => {
    if (!state.themeData.id) return;
    if (state.themeData.childrenList && state.themeData.childrenList.length > 0) return;

    let cancelled = false;

    getAgreementData()
      .then((agreement: IAgreement & { children?: unknown }) => {
        if (cancelled) return;

        const children = agreement.children ?? agreement.childrenList;
        if (!children || !Array.isArray(children) || children.length === 0) return;

        // Garante que o header e o localStorage sejam atualizados assim que
        // tivermos o agreement real do staff logado.
        if (agreement.id) {
          setAxiosHeaders(agreement.id);
          localStorage.setItem('agreementId', JSON.stringify(agreement.id));
        }

        setState(prev => ({
          ...prev,
          themeData: {
            ...prev.themeData,
            id: agreement.id ?? prev.themeData.id,
            name: agreement.name ?? prev.themeData.name,
            integrationId: agreement.integrationId ?? prev.themeData.integrationId,
            childrenList: children,
          },
        }));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [state.themeData.id, state.themeData.childrenList]);

  useEffect(() => {
    let cleanupAnimations: (() => void) | undefined;

    themeResolver().then((agreement: Partial<IThemeContext>) => {
      setState(prev => ({
        ...prev,
        themeData: { ...prev.themeData, ...agreement },
      }));

      setAxiosHeaders(agreement.id || null);
      localStorage.setItem('agreementId', JSON.stringify(agreement.id ?? null));

      cleanupAnimations = createSetupAnimations(setState);
    });

    return () => {
      cleanupAnimations?.();
    };
  }, [themeResolver]);

  const contextValue = useMemo(
    () => ({
      ...state.themeData,
      ...state.urlData,
      changeTheme,
      setLoading,
      setMenuOpen,
      setSidebarCompact,
      updateTheme,
      resetTheme,
      getValidSubscriptions,
    }),
    [
      state.themeData,
      state.urlData,
      changeTheme,
      setLoading,
      setMenuOpen,
      setSidebarCompact,
      updateTheme,
      resetTheme,
      getValidSubscriptions,
    ]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ChakraProvider theme={theme}>
        <DialogProvider>
          <LoadingScreen
            description={state.loadingData.loadingDescription}
            isOpen={state.loadingData.isLoading}
            loadingDelay={state.loadingData.loadingDelay}
          />

          {!state.setupData.exit && (
            <Fade
              unmountOnExit
              transition={{ exit: { duration: 0.2 }, enter: { duration: 0 } }}
              in={!state.themeData.logo}
            >
              <Center
                pos="fixed"
                top="0"
                left="0"
                width="100%"
                height="100vh"
                pointerEvents="none"
              >
                <CircularProgress margin="auto" color="black" isIndeterminate />
              </Center>
            </Fade>
          )}

          <Fade
            transition={{ exit: { duration: 0.5 }, enter: { duration: 1, delay: 0.5 } }}
            in={!!(state.themeData.logo && !state.setupData.exit)}
          >
            <Center
              pos="fixed"
              top="0"
              left="0"
              width="100%"
              height="100vh"
              pointerEvents="none"
              zIndex={1000}
            >
              <Box
                width="50px"
                height="50px"
                borderRadius="50%"
                pos="absolute"
                backgroundColor={state.themeData.colors.primaryColor}
                transition="2s"
                transform={state.setupData.animate ? 'scale(100)' : 'scale(0)'}
              />
              <Image
                maxWidth="500px"
                src={state.themeData.logo}
                w="70%"
                margin="auto"
                transition="0.1s"
              />
            </Center>
          </Fade>

          <Fade in={state.setupData.exit} transition={{ enter: { duration: 0.2 } }}>
            {!state.refreshChildren ? children : null}
          </Fade>
        </DialogProvider>
      </ChakraProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
